import { BeheerVestiging, useGetBeheerVestigingen } from "@/entities/vestiging";
import { StationsAutocomplete } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
} from "@/shared/components";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

interface AddStationDialogProps {
    open: boolean;
    onClose: () => void;
    onAddStation: (stationCode: string) => Promise<void>;
}

interface StationFormData extends Record<string, unknown> {
    stationCode: string;
}

export function AddStationDialog({ open, onClose, onAddStation }: Readonly<AddStationDialogProps>) {
    const { data: beheerVestigingen = [], isPending, error } = useGetBeheerVestigingen();

    const filterInzetbareStationsFunction = useMemo(
        () => BeheerVestiging.createInzetbareStationsFilterFn(beheerVestigingen),
        [beheerVestigingen]
    );

    const form = useForm<StationFormData>({
        defaultValues: {
            stationCode: "",
        },
    });

    if (isPending) {
        return null;
    }

    if (error) {
        return <p>Fout bij het laden van vestigingen: {error.message}</p>;
    }

    const handleSubmit = async (data: StationFormData) => {
        if (data.stationCode) {
            await onAddStation(data.stationCode);
            form.reset();
            onClose();
        }
    };

    const handleCancel = () => {
        form.reset();
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContainer className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Station toevoegen</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            void form.handleSubmit(handleSubmit)(e);
                        }}
                    >
                        <DialogBody className="grid gap-2">
                            <DialogDescription>Selecteer een station om toe te voegen aan de faciliteitenaanvraag.</DialogDescription>
                            <StationsAutocomplete
                                form={form}
                                name="stationCode"
                                placeholder="Zoek station..."
                                nlStationsOnly
                                filterFunction={filterInzetbareStationsFunction}
                                data-testid="vestiging-add-station"
                            />
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="secondary" onClick={handleCancel}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary">
                                Toevoegen
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
